import { DataState } from '../interfaces/commonInterfaces';
import { message } from 'antd';
import { Fonts } from '../utils/constants';

/**
 * Extrae la fecha de nacimiento del RFC (posiciones 4-9) y calcula la edad actual.
 * Se usa para verificar que el titular sea mayor de 18 años.
 * Retorna null si el RFC no tiene el formato esperado.
 */
export function calcEdadDesdeRFC(rfc: string): number | null {
    if (rfc.length < 10) return null;
    const yy = parseInt(rfc.substring(4, 6), 10);
    const mm = parseInt(rfc.substring(6, 8), 10);
    const dd = parseInt(rfc.substring(8, 10), 10);
    if (isNaN(yy) || isNaN(mm) || isNaN(dd)) return null;
    const currentYY = new Date().getFullYear() % 100;
    const year = yy > currentYY ? 1900 + yy : 2000 + yy;
    const birthDate = new Date(year, mm - 1, dd);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
    return age;
}

function getFirstVowel(text: string): string | null {
    const accentMap: Record<string, string> = { 'Á': 'A', 'É': 'E', 'Í': 'I', 'Ó': 'O', 'Ú': 'U', 'Ü': 'U' };
    for (const char of text.toUpperCase()) {
        if ('AEIOUÁÉÍÓÚÜ'.includes(char)) {
            return accentMap[char] ?? char;
        }
    }
    return null;
}

/**
 * Valida el RFC de Persona Física cruzándolo con el nombre del titular.
 * Reglas aplicadas:
 *   - Posición 0: primera letra del apellido paterno
 *   - Posición 1: primera vocal del apellido paterno
 *   - Posición 2: primera letra del apellido materno
 *   - Posición 3: inicial del segundo nombre o del primer nombre 
 *   - La edad calculada desde el RFC debe ser >= 18 años
 */
export function validateRFCFisicaConNombre(
    rfc: string,
    apellidoPaterno: string,
    apellidoMaterno: string,
    personLabel: string = 'la persona física',
    primerNombre?: string,
    segundoNombre?: string
): { valid: boolean; errorMsg: string } {
    if (rfc.length < 13) return { valid: false, errorMsg: 'RFC No válido' };
    if (apellidoPaterno && apellidoPaterno.length > 0) {
        if (rfc[0].toUpperCase() !== apellidoPaterno[0].toUpperCase()) {
            return { valid: false, errorMsg: `El RFC no coincide con ${personLabel}` };
        }
        const firstVowel = getFirstVowel(apellidoPaterno);
        if (firstVowel && rfc[1].toUpperCase() !== firstVowel) {
            return { valid: false, errorMsg: `El RFC no coincide con ${personLabel}` };
        }
    }
    if (apellidoMaterno && apellidoMaterno.length > 0) {
        if (rfc[2].toUpperCase() !== apellidoMaterno[0].toUpperCase()) {
            return { valid: false, errorMsg: `El RFC no coincide con ${personLabel}` };
        }
    }
    if (primerNombre && primerNombre.length > 0) {
        const rfcPos3 = rfc[3].toUpperCase();
        const matchesPrimero = rfcPos3 === primerNombre[0].toUpperCase();
        if (segundoNombre && segundoNombre.length > 0) {
            const matchesSegundo = rfcPos3 === segundoNombre[0].toUpperCase();
            if (!matchesPrimero && !matchesSegundo) {
                return { valid: false, errorMsg: `El RFC no coincide con ${personLabel}` };
            }
        } else if (!matchesPrimero) {
            return { valid: false, errorMsg: `El RFC no coincide con ${personLabel}` };
        }
    }
    const age = calcEdadDesdeRFC(rfc);
    if (age === null || age < 18) {
        return { valid: false, errorMsg: 'Debe ser mayor de edad'};
    }
    return { valid: true, errorMsg: '' };
}

export function ValidateCreditFirstStep(dataCotizador: DataState) : boolean {
    let statusFirstStep = true;

    if(dataCotizador.productTypeOption === undefined){
        message.destroy();
        message.error({ content: 'Por favor, seleccione un tipo de financiemiento.', style: { fontFamily: Fonts.sspRegular } });
        statusFirstStep = false;
    }else if(dataCotizador.credit === undefined || dataCotizador.credit === 0){
        message.destroy();
        message.error({ content: 'Por favor, ingrese un monto de crédito.', style: { fontFamily: Fonts.sspRegular } });
        statusFirstStep = false;
    }else if(dataCotizador.plazo === 0){
        message.destroy();
        message.error({ content: 'Por favor, seleccione un plazo.', style: { fontFamily: Fonts.sspRegular } });
        statusFirstStep = false;
    }
    return statusFirstStep
}

export function ValidateLeasingFirstStep(dataCotizador: DataState): boolean {
      let statusFirstStep = true;

      if (dataCotizador.userActivo < 2) {
        if (dataCotizador.assetPrice === undefined || dataCotizador.assetPrice === 0) {
            message.destroy();
            message.error({ content: 'Por favor, ingresa un precio válido.', style: { fontFamily: Fonts.sspRegular } });
            statusFirstStep = false;
        // Monto mínimo del activo: $200,000 MXN
        } else if (dataCotizador.assetPrice < 200000) {
            message.destroy();
            message.error({ content: 'No se alcanza el monto mínimo de $200,000 MXN', style: { fontFamily: Fonts.sspRegular } });
            statusFirstStep = false;
        // Se bloquea si el monto supera $350k directamente O si el flag de error está activo
        // (el flag cubre el caso de activos  donde el 35% < $350k pero el usuario
        //  capturó un monto mayor a $350k en el input)
        } else if ((dataCotizador.assetDownPayment !== undefined && dataCotizador.assetDownPayment > 350000) || dataCotizador.assetDownPaymentHasError) {
            message.destroy();
            message.error({ content: 'El pago inicial no debe superar $350,000 MXN o el 35% del valor del vehículo', style: { fontFamily: Fonts.sspRegular } });
            statusFirstStep = false;
        } else if (dataCotizador.userActivo === undefined) {
            message.destroy();
            message.error({ content: 'Por favor, selecciona un plazo.', style: { fontFamily: Fonts.sspRegular } });
            statusFirstStep = false;
        } else if (dataCotizador.purchaseOption === "_") {
            message.destroy();
            message.error({ content: 'Por favor, seleccione el valor de compra', style: { fontFamily: Fonts.sspRegular } });
            statusFirstStep = false;
        }
      } else {
        if (dataCotizador.assetPrice === undefined || dataCotizador.assetPrice === 0) {
            message.destroy();
            message.error({ content: 'Por favor, ingresa un precio válido.', style: { fontFamily: Fonts.sspRegular } });
            statusFirstStep = false;
        } else if (dataCotizador.assetPrice < 200000) {
            message.destroy();
            message.error({ content: 'No se alcanza el monto mínimo de $200,000 MXN', style: { fontFamily: Fonts.sspRegular } });
            statusFirstStep = false;
        } else if ((dataCotizador.assetDownPayment !== undefined && dataCotizador.assetDownPayment > 350000) || dataCotizador.assetDownPaymentHasError) {
            message.destroy();
            message.error({ content: 'El pago inicial no debe superar $350,000 MXN o el 35% del valor del vehículo', style: { fontFamily: Fonts.sspRegular } });
            statusFirstStep = false;
        } else if (dataCotizador.userActivo === undefined) {
            message.destroy();
            message.error({ content: 'Por favor, selecciona un plazo.', style: { fontFamily: Fonts.sspRegular } });
            statusFirstStep = false;
        } else if (dataCotizador.purchaseOption === "_") {
            message.destroy();
            message.error({ content: 'Por favor, seleccione el valor de compra', style: { fontFamily: Fonts.sspRegular } });
            statusFirstStep = false;
        }
      }

      return statusFirstStep;
    }

export function ValidateThirdStep(dataCotizador: DataState): boolean {
      let statusThirdStep = true;
      if (dataCotizador.userType > 0) {
            if (dataCotizador.userType === undefined) {
                  message.destroy();
                  message.error({ content: 'Por favor, seleccione un tipo de usuario', style: { fontFamily: Fonts.sspRegular } });
                  statusThirdStep = false;
            } else if (dataCotizador.userRFC === "" || !dataCotizador.validateUserRFC) {
                  message.destroy();
                  message.error({ content: 'Por favor, ingrese un RFC válido', style: { fontFamily: Fonts.sspRegular } });
                  statusThirdStep = false;
            } else if (dataCotizador.userFirstName === "") {
                  message.destroy();
                  message.error({ content: 'Por favor, ingrese su nombre', style: { fontFamily: Fonts.sspRegular } });
                  statusThirdStep = false;
            } else if (dataCotizador.userFirstLastName === "") {
                  message.destroy();
                  message.error({ content: 'Por favor, ingrese su primer apellido', style: { fontFamily: Fonts.sspRegular } });
                  statusThirdStep = false;
            } else if (dataCotizador.userSecondLastName === "") {
                  message.destroy();
                  message.error({ content: 'Por favor, ingrese su segundo apellido', style: { fontFamily: Fonts.sspRegular } });
                  statusThirdStep = false;
            } else if (dataCotizador.userGender === "") {
                  message.destroy();
                  message.error({ content: 'Por favor, seleccione el género', style: { fontFamily: Fonts.sspRegular } });
                  statusThirdStep = false;
            }else if (dataCotizador.userEmail === "" || !dataCotizador.validateUserEmail) {
                  message.destroy();
                  message.error({ content: 'Por favor, ingrese un correo electrónico válido', style: { fontFamily: Fonts.sspRegular } });
                  statusThirdStep = false;
            } else if (dataCotizador.userCellphone === "" || !(dataCotizador.userCellphone.length >= 10)) {
                  message.destroy();
                  message.error({ content: 'Por favor, ingrese un número valido de 10 dígitos', style: { fontFamily: Fonts.sspRegular } });
                  statusThirdStep = false;
            }
            else if (dataCotizador.userCP === "") {
                  message.destroy();
                  message.error({ content: 'Por favor, ingrese un CP válido', style: { fontFamily: Fonts.sspRegular } });
                  statusThirdStep = false;
            } else if (dataCotizador.userState === "") {
                  message.destroy();
                  message.error({ content: 'Por favor, ingrese el estado', style: { fontFamily: Fonts.sspRegular } });
                  statusThirdStep = false;
            } else if (dataCotizador.userMunicipality === "") {
                  message.destroy();
                  message.error({ content: 'Por favor, ingrese el municipio', style: { fontFamily: Fonts.sspRegular } });
                  statusThirdStep = false;
            }
            else if (dataCotizador.userContactMethod === "") {
                  message.destroy();
                  message.error({ content: 'Por favor, seleccione un método de contacto', style: { fontFamily: Fonts.sspRegular } });
                  statusThirdStep = false;
            }
      } else {
            if (dataCotizador.userType === undefined) {
                  message.destroy();
                  message.error({ content: 'Por favor, seleccione un tipo de usuario', style: { fontFamily: Fonts.sspRegular } });
                  statusThirdStep = false;
            } else if (dataCotizador.userRFC === "" || !dataCotizador.validateUserRFC) {
                  message.destroy();
                  message.error({ content: 'Por favor, ingrese un RFC válido', style: { fontFamily: Fonts.sspRegular } });
                  statusThirdStep = false;
            } else if (dataCotizador.userEnterprise === "") {
                  message.destroy();
                  message.error({ content: 'Por favor, ingrese el nombre de la empresa', style: { fontFamily: Fonts.sspRegular } });
                  statusThirdStep = false;
            }
            else if (dataCotizador.userCP === "") {
                  message.destroy();
                  message.error({ content: 'Por favor, ingrese un CP válido', style: { fontFamily: Fonts.sspRegular } });
                  statusThirdStep = false;
            } else if (dataCotizador.userState === "") {
                  message.destroy();
                  message.error({ content: 'Por favor, ingrese el estado', style: { fontFamily: Fonts.sspRegular } });
                  statusThirdStep = false;
            } else if (dataCotizador.userMunicipality === "") {
                  message.destroy();
                  message.error({ content: 'Por favor, ingrese el municipio', style: { fontFamily: Fonts.sspRegular } });
                  statusThirdStep = false;
            }
            else if (dataCotizador.userContactMethod === "") {
                  message.destroy();
                  message.error({ content: 'Por favor, seleccione un método de contacto', style: { fontFamily: Fonts.sspRegular } });
                  statusThirdStep = false;
            } else if (dataCotizador.legalRepresentativeFirstName === "") {
                  message.destroy();
                  message.error({ content: 'Por favor, ingrese el nombre del representante', style: { fontFamily: Fonts.sspRegular } });
                  statusThirdStep = false;
            } else if (dataCotizador.legalRepresentativeFirstLastName === "") {
                  message.destroy();
                  message.error({ content: 'Por favor, ingrese el primer apellido del representante', style: { fontFamily: Fonts.sspRegular } });
                  statusThirdStep = false;
            } else if (dataCotizador.legalRepresentativeSecondLastName === "") {
                  message.destroy();
                  message.error({ content: 'Por favor, ingrese el segundo apellido del representante', style: { fontFamily: Fonts.sspRegular } });
                  statusThirdStep = false;
            // Validación del RFC del Representante Legal (Persona Moral):
            // se verifica formato + coincidencia con apellidos + mayoría de edad
            } else if (dataCotizador.legalRepresentativeRFC === "" || !dataCotizador.validateLegalRepresentativeRFC) {
                  message.destroy();
                  message.error({ content: 'Por favor, ingrese un RFC válido del representante legal', style: { fontFamily: Fonts.sspRegular } });
                  statusThirdStep = false;
            } else if (dataCotizador.legalRepresentativeEmail === "" || !dataCotizador.validatelegalRepresentativeEmail) {
                  message.destroy();
                  message.error({ content: 'Por favor, ingrese un correo electrónico válido', style: { fontFamily: Fonts.sspRegular } });
                  statusThirdStep = false;
            } else if (dataCotizador.legalRepresentativeCellphone === "" || !(dataCotizador.legalRepresentativeCellphone.length >= 10)) {
                  message.destroy();
                  message.error({ content: 'Por favor, ingrese un número valido de 10 dígitos', style: { fontFamily: Fonts.sspRegular } });
                  statusThirdStep = false;
            }
      }
      // Validación adicional del RFC de Persona Física: se compara con apellidos ingresados
      // y se verifica que la edad sea >= 18 años. Se ejecuta fuera de la cadena if/else
      // para no interferir con las validaciones de campos vacíos anteriores
      if (statusThirdStep && dataCotizador.userType === 1) {
            const rfcCheck = validateRFCFisicaConNombre(dataCotizador.userRFC, dataCotizador.userFirstLastName, dataCotizador.userSecondLastName, 'la persona física', dataCotizador.userFirstName, dataCotizador.userSecondName);
            if (!rfcCheck.valid) {
                  message.destroy();
                  message.error({ content: rfcCheck.errorMsg, style: { fontFamily: Fonts.sspRegular } });
                  statusThirdStep = false;
            }
      }
      if (statusThirdStep && dataCotizador.userType === 0 && dataCotizador.legalRepresentativeRFC.length === 13) {
            const rfcCheck = validateRFCFisicaConNombre(dataCotizador.legalRepresentativeRFC, dataCotizador.legalRepresentativeFirstLastName, dataCotizador.legalRepresentativeSecondLastName, 'el representante legal', dataCotizador.legalRepresentativeFirstName, dataCotizador.legalRepresentativeSecondName);
            if (!rfcCheck.valid) {
                  message.destroy();
                  message.error({ content: rfcCheck.errorMsg, style: { fontFamily: Fonts.sspRegular } });
                  statusThirdStep = false;
            }
      }
      return statusThirdStep;
}

export function ValidateFifthStep(dataCotizador: DataState): boolean {
      let statusStep = true;

      if (dataCotizador.avalRFC === "" || !dataCotizador.validateAvalRFC) {
            message.destroy();
            message.error({ content: 'Por favor, ingrese un RFC válido', style: { fontFamily: Fonts.sspRegular } });
            statusStep = false;
      } else if (dataCotizador.avalFirstName === "") {
            message.destroy();
            message.error({ content: 'Por favor, ingrese el nombre del aval', style: { fontFamily: Fonts.sspRegular } });
            statusStep = false;
      } else if (dataCotizador.avalFirstLastName === "") {
            message.destroy();
            message.error({ content: 'Por favor, ingrese el primer apellido del aval', style: { fontFamily: Fonts.sspRegular } });
            statusStep = false;
      } else if (dataCotizador.avalSecondLastName === "") {
            message.destroy();
            message.error({ content: 'Por favor, ingrese el segundo apellido del aval', style: { fontFamily: Fonts.sspRegular } });
            statusStep = false;
      } else if (dataCotizador.avalGender === "") {
            message.destroy();
            message.error({ content: 'Por favor, seleccione el género', style: { fontFamily: Fonts.sspRegular } });
            statusStep = false;
      } else if (dataCotizador.avalEmail === "" || !dataCotizador.validateAvalEmail) {
            message.destroy();
            message.error({ content: 'Por favor, ingrese un correo electrónico válido', style: { fontFamily: Fonts.sspRegular } });
            statusStep = false;
      } else if (dataCotizador.avalCellphone === "" || !(dataCotizador.avalCellphone.length >= 10)) {
            message.destroy();
            message.error({ content: 'Por favor, ingrese un número valido de 10 dígitos', style: { fontFamily: Fonts.sspRegular } });
            statusStep = false;
      }
      else if (dataCotizador.avalCP === "") {
            message.destroy();
            message.error({ content: 'Por favor, ingrese un CP válido', style: { fontFamily: Fonts.sspRegular } });
            statusStep = false;
      } else if (dataCotizador.avalState === "") {
            message.destroy();
            message.error({ content: 'Por favor, ingrese el estado', style: { fontFamily: Fonts.sspRegular } });
            statusStep = false;
      }else if (dataCotizador.avalMunicipality === "") {
            message.destroy();
            message.error({ content: 'Por favor, ingrese el municipio', style: { fontFamily: Fonts.sspRegular } });
            statusStep = false;
      }
      // Validación del RFC del Aval: coincidencia con apellidos + mayoría de edad
      if (statusStep && dataCotizador.avalRFC.length === 13) {
            const rfcCheck = validateRFCFisicaConNombre(dataCotizador.avalRFC, dataCotizador.avalFirstLastName, dataCotizador.avalSecondLastName, 'el principal accionista/aval', dataCotizador.avalFirstName, dataCotizador.avalSecondName);
            if (!rfcCheck.valid) {
                  message.destroy();
                  message.error({ content: rfcCheck.errorMsg, style: { fontFamily: Fonts.sspRegular } });
                  statusStep = false;
            }
      }
      return statusStep;
  }

  export function ValidateCotizacion(dataCotizador: DataState) : boolean {
      let statusFirstStep = true;
            dataCotizador.assetPrice === undefined || dataCotizador.assetPrice === 0
              ? statusFirstStep = false
          : dataCotizador.userPlazo === undefined
              ? statusFirstStep = false
          : dataCotizador.purchaseOption === "_"
              ? statusFirstStep = false
          : statusFirstStep = true;

      return statusFirstStep
  }

  export function ValidateCotizacionPyme(dataCotizador: DataState): boolean {
      let statusStep = true;
      dataCotizador.assetPrice === undefined || dataCotizador.assetPrice === 0
              ? statusStep = false
          : dataCotizador.userPlazo === undefined
              ? statusStep = false
          : dataCotizador.purchaseOption === "_"
              ? statusStep = false
          : statusStep = true;

      return statusStep
  }